import { t } from '@lingui/macro'
import CloseIcon from '@mui/icons-material/Close'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format, parse } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AspenCandidatesService, CandidateDTO, CarePartnerResponseDTO, CarePartnersService } from '../client'
import { useCreateAndAssignCarePartner } from '../hooks/useCreateAndAssignCarePartner'
import { CandidateSearchDropdown } from './CandidateSearchDropdown'

dayjs.extend(utc)
dayjs.extend(timezone)

// Time slot definitions matching central-site
interface TimeSlot {
    id: string
    labelKey: string
    startHour: number
    endHour: number
}

const TIME_SLOTS: TimeSlot[] = [
    {
        id: 'morning-early',
        labelKey: '9:00 AM – 11:00 AM',
        startHour: 9,
        endHour: 11
    },
    {
        id: 'morning-late',
        labelKey: '11:00 AM – 1:00 PM',
        startHour: 11,
        endHour: 13
    },
    {
        id: 'afternoon-early',
        labelKey: '1:00 PM – 3:00 PM',
        startHour: 13,
        endHour: 15
    },
    {
        id: 'afternoon-late',
        labelKey: '3:00 PM – 5:00 PM',
        startHour: 15,
        endHour: 17
    }
]

const getTimezoneOptions = () => [
    { value: 'ET', label: t`Eastern Time (ET)` },
    { value: 'CT', label: t`Central Time (CT)` },
    { value: 'MT', label: t`Mountain Time (MT)` },
    { value: 'PT', label: t`Pacific Time (PT)` }
]

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const TIMEZONE_MAPPING: Record<string, string> = {
    ET: 'America/New_York',
    CT: 'America/Chicago',
    MT: 'America/Denver',
    PT: 'America/Los_Angeles'
}

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Mx.'] as const

const convertToUTC = (localTime: Dayjs, timezone: string): Dayjs => {
    const tzString = TIMEZONE_MAPPING[timezone]
    if (!tzString) {
        throw new Error(t`Invalid timezone: ${timezone}`)
    }
    return localTime.tz(tzString).utc()
}

interface CreateCarePartnerModalProps {
    open: boolean
    onClose: () => void
    onSuccess?: (carePartnerId: string) => void
    candidateId?: string
    mode?: 'create' | 'edit'
    carePartner?: CarePartnerResponseDTO | null
    assignedCandidateId?: string // ID of the currently assigned candidate (if any)
}

interface FormData {
    title?: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    assignedCandidate: CandidateDTO | null
    dateOfBirth: string | null
    preferredContactDate: Date | null
    preferredTimeSlot: string
    timezone: string
    languagePreference: string
    preferredName?: string
    smsConsent: boolean
}

// Email validation using the exact same pattern as central-site (Yup's email validation)
const isValidEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') return false

    // Use the same simple email validation as central-site
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

// Phone validation using the exact same pattern as central-site
const isValidPhone = (phone: string): boolean => {
    if (!phone || typeof phone !== 'string') return false
    // Exact same regex as central-site: /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/
    const phoneRegex = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/
    return phoneRegex.test(phone)
}

const validateForm = (data: FormData, requireCandidate: boolean = true): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!data.firstName?.trim()) {
        errors.firstName = t`First name is required`
    } else if (data.firstName.length > 50) {
        errors.firstName = t`First name must be 50 characters or less`
    }
    if (!data.lastName?.trim()) {
        errors.lastName = t`Last name is required`
    } else if (data.lastName.length > 50) {
        errors.lastName = t`Last name must be 50 characters or less`
    }
    // Phone validation - exact same as central-site
    if (!data.phoneNumber?.trim()) {
        errors.phoneNumber = t`Phone number is required`
    } else if (!isValidPhone(data.phoneNumber)) {
        errors.phoneNumber = t`Format 123-456-7890`
    }

    // Email validation - same as central-site (Yup's email validation)
    if (data.email && !isValidEmail(data.email)) {
        errors.email = t`Invalid email address`
    } else if (data.email && data.email.length > 256) {
        errors.email = t`Email must be 256 characters or less`
    }

    if (requireCandidate && !data.assignedCandidate) {
        errors.assignedCandidate = t`Please select a candidate`
    }

    // Preferred Contact Time validation: if date is selected, time slot must be selected
    if (data.preferredContactDate && !data.preferredTimeSlot) {
        errors.preferredTimeSlot = t`Time slot is required when a date is selected`
    }

    // Preferred Contact Time validation: if time slot is selected, date must be selected
    if (data.preferredTimeSlot && !data.preferredContactDate) {
        errors.preferredContactDate = t`Date is required when a time slot is selected`
    }

    return errors
}

export const CreateCarePartnerModal: React.FC<CreateCarePartnerModalProps> = ({
    open,
    onClose,
    onSuccess,
    candidateId,
    mode = 'create',
    carePartner,
    assignedCandidateId
}) => {
    const { createAndAssign, updateCarePartnerData, loading, error } = useCreateAndAssignCarePartner()
    const [submitError, setSubmitError] = useState<string | null>(null)

    // Refs for focusing on fields with errors
    const preferredContactDateRef = useRef<HTMLInputElement>(null)
    const preferredTimeSlotRef = useRef<HTMLDivElement>(null)

    // Function to fetch candidate data for edit mode
    const fetchCandidateData = useCallback(async (candidateId: string) => {
        try {
            const response = await AspenCandidatesService.getCandidatesCandidateIdGet({
                candidateId
            })
            return response
        } catch (err) {
            console.error(t`Failed to fetch candidate data:`, err)
            return null
        }
    }, [])

    // Form state
    const [formData, setFormData] = useState<FormData>({
        title: undefined,
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        assignedCandidate: null,
        dateOfBirth: null,
        preferredContactDate: null,
        preferredTimeSlot: '',
        timezone: 'ET',
        languagePreference: '',
        smsConsent: false
    })

    const [errors, setErrors] = useState<Record<string, string>>({})

    const assignedCandidate = formData.assignedCandidate

    // Reset form when modal opens/closes
    useEffect(() => {
        const initializeForm = async () => {
            if (open) {
                if (mode === 'edit' && carePartner) {
                    // Edit mode: populate with existing data
                    // Extract date and time slot from existing datetimes
                    const extractDateAndTimeSlot = (
                        startDatetime: string | null | undefined,
                        endDatetime: string | null | undefined
                    ): { date: Date | null; timeSlot: string; timezone: string } => {
                        if (!startDatetime || !endDatetime) {
                            return { date: null, timeSlot: '', timezone: 'ET' }
                        }

                        try {
                            // Parse the UTC datetime strings
                            const start = dayjs.utc(startDatetime)
                            const end = dayjs.utc(endDatetime)

                            // Convert to ET for display (default timezone)
                            // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                            const startET = start.tz('America/New_York')
                            // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                            const endET = end.tz('America/New_York')

                            // Extract date
                            const date = startET.toDate()

                            // Extract hour to determine time slot
                            const startHour = startET.hour()
                            const endHour = endET.hour()

                            // Match to time slot by finding the slot that matches start and end hours
                            const matchedSlot = TIME_SLOTS.find((slot) => slot.startHour === startHour && slot.endHour === endHour)
                            const timeSlot = matchedSlot ? matchedSlot.id : ''

                            return { date, timeSlot, timezone: 'ET' }
                        } catch {
                            return { date: null, timeSlot: '', timezone: 'ET' }
                        }
                    }

                    const {
                        date: preferredDate,
                        timeSlot,
                        timezone: preferredTimezone
                    } = extractDateAndTimeSlot(carePartner.preferredContactStartDatetime, carePartner.preferredContactEndDatetime)

                    if (candidateId) {
                        // If candidateId is provided, fetch real candidate info
                        try {
                            const candidateData = await fetchCandidateData(candidateId)
                            setFormData({
                                title: carePartner.title || undefined,
                                firstName: carePartner.firstName || '',
                                lastName: carePartner.lastName || '',
                                phoneNumber: carePartner.phoneNumber || '',
                                email: carePartner.email || '',
                                assignedCandidate: candidateData || null,
                                dateOfBirth: carePartner.dateOfBirth || null,
                                preferredContactDate: preferredDate,
                                preferredTimeSlot: timeSlot,
                                timezone: preferredTimezone,
                                languagePreference: carePartner.languagePreference || '',
                                preferredName: carePartner.preferredName || undefined,
                                smsConsent: carePartner.smsConsent || false
                            })
                        } catch (err) {
                            // Fallback to placeholder if candidate fetch fails
                            setFormData({
                                title: carePartner.title || undefined,
                                firstName: carePartner.firstName || '',
                                lastName: carePartner.lastName || '',
                                phoneNumber: carePartner.phoneNumber || '',
                                email: carePartner.email || '',
                                assignedCandidate: null,
                                dateOfBirth: carePartner.dateOfBirth || null,
                                preferredContactDate: preferredDate,
                                preferredTimeSlot: timeSlot,
                                timezone: preferredTimezone,
                                languagePreference: carePartner.languagePreference || '',
                                smsConsent: carePartner.smsConsent || false
                            })
                        }
                    } else {
                        // No specific candidate context - allow user to select any candidate
                        setFormData({
                            title: carePartner.title || undefined,
                            firstName: carePartner.firstName || '',
                            lastName: carePartner.lastName || '',
                            phoneNumber: carePartner.phoneNumber || '',
                            email: carePartner.email || '',
                            assignedCandidate: null,
                            dateOfBirth: carePartner.dateOfBirth || null,
                            preferredContactDate: preferredDate,
                            preferredTimeSlot: timeSlot,
                            timezone: preferredTimezone,
                            languagePreference: carePartner.languagePreference || '',
                            preferredName: carePartner.preferredName || undefined,
                            smsConsent: carePartner.smsConsent || false
                        })
                    }
                } else {
                    // Create mode: empty form
                    setFormData({
                        firstName: '',
                        lastName: '',
                        phoneNumber: '',
                        email: '',
                        assignedCandidate: null,
                        dateOfBirth: null,
                        preferredContactDate: null,
                        preferredTimeSlot: '',
                        timezone: 'ET',
                        languagePreference: '',
                        preferredName: undefined,
                        smsConsent: false
                    })
                }
                setErrors({})
                setSubmitError(null)
            }
        }

        initializeForm()
    }, [open, mode, carePartner, candidateId, fetchCandidateData])

    const handleClose = () => {
        if (!loading) {
            onClose()
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validate form - require candidate only if not in edit mode without candidateId
        const requireCandidate = !(mode === 'edit' && !candidateId)
        const newErrors = validateForm(formData, requireCandidate)
        setErrors(newErrors)

        if (Object.keys(newErrors).length > 0) {
            // Focus on the first field with an error
            if (newErrors.preferredContactDate && preferredContactDateRef.current) {
                setTimeout(() => {
                    preferredContactDateRef.current?.focus()
                }, 100)
            } else if (newErrors.preferredTimeSlot && preferredTimeSlotRef.current) {
                setTimeout(() => {
                    preferredTimeSlotRef.current?.focus()
                }, 100)
            }
            return
        }

        try {
            setSubmitError(null)

            // Calculate preferred contact datetimes from date + time slot + timezone
            const calculatePreferredContactDatetimes = (): {
                preferredContactStartDatetime: string | undefined
                preferredContactEndDatetime: string | undefined
            } => {
                if (!formData.preferredContactDate || !formData.preferredTimeSlot) {
                    return {
                        preferredContactStartDatetime: undefined,
                        preferredContactEndDatetime: undefined
                    }
                }

                const timeSlot = TIME_SLOTS.find((slot) => slot.id === formData.preferredTimeSlot)
                if (!timeSlot) {
                    return {
                        preferredContactStartDatetime: undefined,
                        preferredContactEndDatetime: undefined
                    }
                }

                // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                const userTimezone = TIMEZONE_MAPPING[formData.timezone] || 'America/New_York'
                const dateString = format(formData.preferredContactDate, 'yyyy-MM-dd')
                const localDate = dayjs.tz(dateString, userTimezone)
                const startDatetime = localDate.hour(timeSlot.startHour).minute(0).second(0)
                const endDatetime = localDate.hour(timeSlot.endHour).minute(0).second(0)

                // Convert to UTC
                const utcStartDatetime = convertToUTC(startDatetime, formData.timezone)
                const utcEndDatetime = convertToUTC(endDatetime, formData.timezone)

                return {
                    preferredContactStartDatetime: utcStartDatetime.toISOString(),
                    preferredContactEndDatetime: utcEndDatetime.toISOString()
                }
            }

            const { preferredContactStartDatetime, preferredContactEndDatetime } = calculatePreferredContactDatetimes()

            let result
            if (mode === 'edit' && carePartner) {
                // Update existing care partner
                result = await updateCarePartnerData(carePartner.id, {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phoneNumber: formData.phoneNumber,
                    email: formData.email || undefined,
                    dateOfBirth: formData.dateOfBirth || undefined,
                    title: formData.title || undefined,
                    preferredName: formData.preferredName || undefined,
                    preferredContactStartDatetime,
                    preferredContactEndDatetime,
                    languagePreference: formData.languagePreference || undefined,
                    smsConsent: formData.smsConsent ? true : undefined
                })

                // If a new candidate is assigned, associate the care partner with that candidate
                // Authenticated request - organizationId is optional, backend uses current_user.organization_id
                if (formData.assignedCandidate?.id) {
                    try {
                        await CarePartnersService.associateCandidateCarePartnersCarePartnerIdCandidateCandidateIdPost({
                            carePartnerId: carePartner.id,
                            candidateId: formData.assignedCandidate.id,
                            organizationId: carePartner.organizationId
                        })
                        console.log(t`Successfully associated care partner with candidate`)
                    } catch (err) {
                        console.error(t`Failed to associate care partner with candidate:`, err)
                        // Don't throw here - the care partner was updated successfully
                    }
                }
            } else {
                // Create new care partner
                result = await createAndAssign(
                    {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phoneNumber: formData.phoneNumber,
                        email: formData.email || undefined,
                        dateOfBirth: formData.dateOfBirth || undefined,
                        title: formData.title || undefined,
                        preferredName: formData.preferredName || undefined,
                        preferredContactStartDatetime,
                        preferredContactEndDatetime,
                        languagePreference: formData.languagePreference || undefined,
                        smsConsent: formData.smsConsent ? true : undefined
                    },
                    formData.assignedCandidate?.id || ''
                )
            }

            // Call success callback with the care partner ID
            if (onSuccess && result) {
                onSuccess(result.id)
            }

            // Close modal on success
            handleClose()
        } catch (err) {
            setSubmitError(
                mode === 'edit' ? t`Failed to update care partner. Please try again.` : t`Failed to create care partner. Please try again.`
            )
        }
    }

    const formatPhoneNumber = (phone: string) => {
        let digits = phone.replace(/\D/g, '')

        if (digits.length > 10) {
            digits = digits.substring(0, 10)
        }

        if (digits.length === 10) {
            return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
        } else if (digits.length > 6) {
            return digits.replace(/(\d{3})(\d{3})/, '$1-$2-')
        } else if (digits.length > 3) {
            return digits.replace(/(\d{3})/, '$1-')
        }
        return phone
    }

    const updateField = (field: keyof FormData, value: any) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value }

            // Clear time slot if date or timezone changes and current slot becomes unavailable
            if (field === 'preferredContactDate' || field === 'timezone') {
                if (updated.preferredContactDate && updated.preferredTimeSlot) {
                    // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                    const userTimezone = TIMEZONE_MAPPING[updated.timezone] || 'America/New_York'
                    // Get current time in the selected timezone
                    const nowInTimezone = dayjs().tz(userTimezone)

                    // Convert selected date to the selected timezone (start of day)
                    const selectedDateInTimezone = dayjs(updated.preferredContactDate).tz(userTimezone).startOf('day')

                    // Check if selected date is today in the selected timezone
                    const isToday = selectedDateInTimezone.isSame(nowInTimezone, 'day')

                    if (isToday) {
                        const timeSlot = TIME_SLOTS.find((slot) => slot.id === updated.preferredTimeSlot)
                        if (timeSlot) {
                            // Create the slot start time in the selected timezone
                            const slotStartTime = selectedDateInTimezone.hour(timeSlot.startHour).minute(0).second(0).millisecond(0)

                            // Clear time slot if it has passed in the selected timezone
                            if (slotStartTime.isBefore(nowInTimezone)) {
                                updated.preferredTimeSlot = ''
                            }
                        }
                    }
                } else if (!updated.preferredContactDate) {
                    // Clear time slot if date is cleared
                    updated.preferredTimeSlot = ''
                }
            }

            return updated
        })

        // Real-time validation for the updated field
        const updatedFormData = { ...formData, [field]: value }
        const requireCandidate = !(mode === 'edit' && !candidateId)
        const fieldErrors = validateForm(updatedFormData, requireCandidate)

        // Only show error for the current field if it has an error
        if (fieldErrors[field]) {
            setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }))
        } else {
            // Clear error if field is now valid
            setErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    const isValid =
        !Object.values(errors).some((error) => error && error.trim() !== '') &&
        formData.firstName.trim() &&
        formData.lastName.trim() &&
        formData.phoneNumber.trim() &&
        (formData.assignedCandidate || (mode === 'edit' && !candidateId))

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='h6' component='div'>
                    {mode === 'edit' ? t`Edit Care Partner` : t`Create Care Partner`}
                </Typography>
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    disabled={loading}
                    sx={{
                        color: (theme) => theme.palette.grey[500]
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {loading && <LinearProgress />}

            <form onSubmit={handleSubmit}>
                <DialogContent dividers sx={{ pt: 2, maxHeight: '80vh', overflowY: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {submitError && (
                            <Alert severity='error' sx={{ mb: 1 }}>
                                {submitError}
                            </Alert>
                        )}

                        {error && (
                            <Alert severity='error' sx={{ mb: 1 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Basic Information Section */}
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 3,
                                bgcolor: 'grey.50',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2.5
                            }}
                        >
                            <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                                {t`Basic Information`}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                                    <FormControl fullWidth error={!!errors.title}>
                                        <InputLabel>{t`Title`}</InputLabel>
                                        <Select
                                            value={formData.title || ''}
                                            onChange={(e) => updateField('title', e.target.value || undefined)}
                                            label={t`Title`}
                                            disabled={loading}
                                            size='medium'
                                        >
                                            <MenuItem value=''>
                                                <em>{t`None`}</em>
                                            </MenuItem>
                                            {TITLE_OPTIONS.map((title) => (
                                                <MenuItem key={title} value={title}>
                                                    {title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.title && (
                                            <Typography variant='caption' color='error' sx={{ mt: 0.5, ml: 1.75 }}>
                                                {errors.title}
                                            </Typography>
                                        )}
                                    </FormControl>

                                    <TextField
                                        label={t`First Name *`}
                                        placeholder={t`Enter first name`}
                                        fullWidth
                                        value={formData.firstName}
                                        onChange={(e) => updateField('firstName', e.target.value)}
                                        error={!!errors.firstName}
                                        helperText={errors.firstName || ''}
                                        disabled={loading}
                                        inputProps={{ maxLength: 50 }}
                                        size='medium'
                                    />
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                                    <TextField
                                        label={t`Last Name *`}
                                        placeholder={t`Enter last name`}
                                        fullWidth
                                        value={formData.lastName}
                                        onChange={(e) => updateField('lastName', e.target.value)}
                                        error={!!errors.lastName}
                                        helperText={errors.lastName || ''}
                                        disabled={loading}
                                        inputProps={{ maxLength: 50 }}
                                        size='medium'
                                    />

                                    <TextField
                                        label={t`Phone Number *`}
                                        placeholder={t`123-456-7890`}
                                        fullWidth
                                        value={formData.phoneNumber}
                                        onChange={(e) => updateField('phoneNumber', formatPhoneNumber(e.target.value))}
                                        error={!!errors.phoneNumber}
                                        helperText={errors.phoneNumber}
                                        disabled={loading}
                                        inputProps={{ maxLength: 12 }}
                                        size='medium'
                                    />
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                                    <TextField
                                        label={t`Email Address`}
                                        placeholder={t`Enter email address`}
                                        type='email'
                                        fullWidth
                                        value={formData.email}
                                        onChange={(e) => updateField('email', e.target.value)}
                                        error={!!errors.email}
                                        helperText={errors.email || ''}
                                        disabled={loading}
                                        inputProps={{ maxLength: 256 }}
                                        size='medium'
                                    />

                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label={t`Date of Birth`}
                                            value={
                                                formData.dateOfBirth
                                                    ? (() => {
                                                          try {
                                                              return parse(formData.dateOfBirth, 'yyyy-MM-dd', new Date())
                                                          } catch {
                                                              return null
                                                          }
                                                      })()
                                                    : null
                                            }
                                            onChange={(newValue) => {
                                                if (newValue) {
                                                    updateField('dateOfBirth', format(newValue, 'yyyy-MM-dd'))
                                                } else {
                                                    updateField('dateOfBirth', null)
                                                }
                                            }}
                                            maxDate={new Date()}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    disabled: loading,
                                                    size: 'medium'
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 3,
                                bgcolor: 'grey.50',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2.5
                            }}
                        >
                            <Box>
                                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                                    {t`Preferred Contact Time`}
                                </Typography>
                                <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.875rem' }}>
                                    {t`Select a date and time slot when you prefer to be contacted`}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={t`Date`}
                                        value={formData.preferredContactDate}
                                        onChange={(newValue) => updateField('preferredContactDate', newValue)}
                                        minDate={new Date()}
                                        maxDate={(() => {
                                            const maxDate = new Date()
                                            maxDate.setDate(maxDate.getDate() + 30)
                                            return maxDate
                                        })()}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                disabled: loading,
                                                size: 'medium',
                                                error: !!errors.preferredContactDate,
                                                helperText: errors.preferredContactDate || '',
                                                inputRef: preferredContactDateRef
                                            }
                                        }}
                                    />
                                </LocalizationProvider>

                                <FormControl fullWidth>
                                    <InputLabel>{t`Time Zone`}</InputLabel>
                                    <Select
                                        value={formData.timezone}
                                        onChange={(e) => updateField('timezone', e.target.value)}
                                        label={t`Time Zone`}
                                        disabled={loading}
                                        size='medium'
                                    >
                                        {getTimezoneOptions().map((tz) => (
                                            <MenuItem key={tz.value} value={tz.value}>
                                                {tz.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <FormControl fullWidth error={!!errors.preferredTimeSlot}>
                                <InputLabel>{t`Time Slot`}</InputLabel>
                                <Select
                                    ref={preferredTimeSlotRef}
                                    value={formData.preferredTimeSlot}
                                    onChange={(e) => updateField('preferredTimeSlot', e.target.value || '')}
                                    label={t`Time Slot`}
                                    disabled={loading || !formData.preferredContactDate}
                                    size='medium'
                                >
                                    <MenuItem value=''>
                                        <em>{formData.preferredContactDate ? t`Select Time Slot` : t`Select a date first`}</em>
                                    </MenuItem>
                                    {TIME_SLOTS.map((slot) => {
                                        // Check if time slot is disabled based on selected date and timezone
                                        const isDisabled = (() => {
                                            if (!formData.preferredContactDate) return false

                                            // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                                            const userTimezone = TIMEZONE_MAPPING[formData.timezone] || 'America/New_York'
                                            // Get current time in the selected timezone
                                            const nowInTimezone = dayjs().tz(userTimezone)

                                            // Convert selected date to the selected timezone (start of day)
                                            const selectedDateInTimezone = dayjs(formData.preferredContactDate).tz(userTimezone).startOf('day')

                                            // Check if selected date is today in the selected timezone
                                            const isToday = selectedDateInTimezone.isSame(nowInTimezone, 'day')

                                            if (isToday) {
                                                // Create the slot start time in the selected timezone
                                                const slotStartTime = selectedDateInTimezone.hour(slot.startHour).minute(0).second(0).millisecond(0)

                                                // Disable if the slot start time has already passed in the selected timezone
                                                return slotStartTime.isBefore(nowInTimezone)
                                            }

                                            return false
                                        })()

                                        return (
                                            <MenuItem key={slot.id} value={slot.id} disabled={isDisabled}>
                                                {slot.labelKey}
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                                {errors.preferredTimeSlot && (
                                    <Typography variant='caption' color='error' sx={{ mt: 0.5, ml: 1.75 }}>
                                        {errors.preferredTimeSlot}
                                    </Typography>
                                )}
                            </FormControl>
                        </Box>

                        {/* Additional Information Section */}
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 3,
                                bgcolor: 'grey.50',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2.5
                            }}
                        >
                            <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                                {t`Additional Information`}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <FormControl fullWidth>
                                    <InputLabel>{t`Language Preference`}</InputLabel>
                                    <Select
                                        value={formData.languagePreference}
                                        onChange={(e) => updateField('languagePreference', e.target.value)}
                                        label={t`Language Preference`}
                                        disabled={loading}
                                        size='medium'
                                    >
                                        <MenuItem value='en'>{t`English`}</MenuItem>
                                        <MenuItem value='es'>{t`Español`}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Preferences & Assignment Section */}
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 3,
                                bgcolor: 'grey.50',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2.5
                            }}
                        >
                            <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                                {t`Preferences & Assignment`}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <TextField
                                    label={t`Preferred Name`}
                                    placeholder={t`Enter preferred name`}
                                    fullWidth
                                    value={formData.preferredName || ''}
                                    onChange={(e) => updateField('preferredName', e.target.value)}
                                    error={!!errors.preferredName}
                                    helperText={errors.preferredName || ''}
                                    disabled={loading}
                                    inputProps={{ maxLength: 50 }}
                                    size='medium'
                                />

                                <FormControl fullWidth error={!!errors.assignedCandidate}>
                                    <CandidateSearchDropdown
                                        value={formData.assignedCandidate}
                                        onChange={(candidate) => updateField('assignedCandidate', candidate)}
                                        error={!!errors.assignedCandidate}
                                        helperText={errors.assignedCandidate}
                                        disabled={loading || (mode === 'edit' && !!candidateId)}
                                        label={mode === 'edit' && !candidateId ? t`Assigned Candidate` : t`Assigned Candidate *`}
                                        placeholder={t`Search candidates by name, DOB, or phone...`}
                                    />
                                </FormControl>

                                {assignedCandidate && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            bgcolor: 'success.50',
                                            border: '1px solid',
                                            borderColor: 'success.200',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography variant='body2' sx={{ fontWeight: 500, mb: 0.5, color: 'success.dark' }}>
                                            {t`Selected Candidate`}
                                        </Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            {assignedCandidate.firstName || t`Unknown`} {assignedCandidate.lastName || t`Unknown`}
                                            {assignedCandidate.dateOfBirth && ` • ${assignedCandidate.dateOfBirth}`}
                                            {assignedCandidate.phone && ` • ${assignedCandidate.phone}`}
                                        </Typography>
                                    </Box>
                                )}

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.smsConsent}
                                            onChange={(e) => updateField('smsConsent', e.target.checked)}
                                            disabled={loading}
                                            color='primary'
                                        />
                                    }
                                    label={t`SMS Consent`}
                                    sx={{ alignItems: 'center' }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={onClose} disabled={loading}>
                        {t`Cancel`}
                    </Button>
                    <Button type='submit' variant='contained' disabled={loading || !isValid} sx={{ minWidth: 120, height: 36 }}>
                        {loading
                            ? mode === 'edit'
                                ? t`Updating...`
                                : t`Creating...`
                            : mode === 'edit'
                            ? t`Update Care Partner`
                            : t`Create Care Partner`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
